/**
 * SEO Auditor - Validates SEO compliance for city landing pages
 */

import { City } from '@/data/cities';
import { CityLanguage } from '@/context/languages/languageData';

export interface SEOAuditResult {
  cityId: string;
  cityName: string;
  language: CityLanguage;
  score: number; // 0-100
  issues: SEOIssue[];
  metrics: SEOMetrics;
  timestamp: Date;
}

export interface SEOIssue {
  severity: 'critical' | 'warning' | 'info';
  category: string;
  message: string;
  recommendation?: string;
}

export interface SEOMetrics {
  // Content metrics
  wordCount: number;
  keywordDensity: number;
  h1Count: number;
  h2Count: number;
  h3Count: number;
  h3PerH2Ratio: number;
  
  // Meta tags
  titleLength: number;
  metaDescriptionLength: number;
  hasCanonical: boolean;
  hasOpenGraph: boolean;
  hasTwitterCard: boolean;
  
  // Schema markup
  hasSchemaFAQ: boolean;
  hasSchemaMedicalBusiness: boolean;
  hasSchemaBreadcrumb: boolean;
  hasSchemaOffer: boolean;
  hasSchemaLocalBusiness: boolean;
  hasSchemaReview: boolean;
  
  // Internal linking
  internalLinkCount: number;
  externalLinkCount: number;
  brokenLinkCount: number;
  
  // Performance
  imageCount: number;
  lazyLoadedImages: number;
  webpImages: number;
  
  // Accessibility
  imagesWithAlt: number;
  semanticHTMLScore: number;
}

/**
 * Audit a city landing page for SEO compliance
 */
export const auditCityLanding = async (
  city: City,
  language: CityLanguage = 'en'
): Promise<SEOAuditResult> => {
  const issues: SEOIssue[] = [];
  let score = 100;

  // Simulate page analysis (in production, this would parse actual DOM/HTML)
  const metrics = await analyzeCityPage(city, language);

  // Check H1
  if (metrics.h1Count !== 1) {
    issues.push({
      severity: 'critical',
      category: 'Headers',
      message: `H1 count is ${metrics.h1Count}, should be exactly 1`,
      recommendation: 'Ensure exactly one H1 tag per page'
    });
    score -= 10;
  }

  // Check H2 sections (should have 19+ categories)
  if (metrics.h2Count < 19) {
    issues.push({
      severity: 'warning',
      category: 'Headers',
      message: `Only ${metrics.h2Count} H2 sections found, recommended 19+`,
      recommendation: 'Add more content categories'
    });
    score -= 5;
  }

  // Check H3 subsections ratio
  if (metrics.h3PerH2Ratio < 3) {
    issues.push({
      severity: 'warning',
      category: 'Headers',
      message: `H3/H2 ratio is ${metrics.h3PerH2Ratio.toFixed(1)}, recommended 3-4+`,
      recommendation: 'Add more subsections (H3) per category'
    });
    score -= 3;
  }

  // Check word count
  if (metrics.wordCount < 1500) {
    issues.push({
      severity: 'critical',
      category: 'Content',
      message: `Word count is ${metrics.wordCount}, minimum 1,500 required`,
      recommendation: 'Expand content to reach minimum word count'
    });
    score -= 15;
  }

  // Check keyword density
  if (metrics.keywordDensity < 1.5 || metrics.keywordDensity > 2.5) {
    issues.push({
      severity: 'warning',
      category: 'Keywords',
      message: `Keyword density is ${metrics.keywordDensity.toFixed(2)}%, recommended 1.5-2.5%`,
      recommendation: 'Adjust keyword usage to optimal range'
    });
    score -= 5;
  }

  // Check meta description
  if (metrics.metaDescriptionLength > 160) {
    issues.push({
      severity: 'warning',
      category: 'Meta Tags',
      message: `Meta description is ${metrics.metaDescriptionLength} characters, max 160 recommended`,
      recommendation: 'Shorten meta description'
    });
    score -= 3;
  }

  if (metrics.metaDescriptionLength < 120) {
    issues.push({
      severity: 'info',
      category: 'Meta Tags',
      message: `Meta description is ${metrics.metaDescriptionLength} characters, 120-160 optimal`,
      recommendation: 'Expand meta description for better CTR'
    });
    score -= 1;
  }

  // Check canonical URL
  if (!metrics.hasCanonical) {
    issues.push({
      severity: 'critical',
      category: 'Meta Tags',
      message: 'Canonical URL is missing',
      recommendation: 'Add canonical URL to prevent duplicate content issues'
    });
    score -= 8;
  }

  // Check Open Graph
  if (!metrics.hasOpenGraph) {
    issues.push({
      severity: 'warning',
      category: 'Meta Tags',
      message: 'Open Graph tags are incomplete',
      recommendation: 'Add complete OG tags for social sharing'
    });
    score -= 4;
  }

  // Check Schema Markup
  const schemaIssues = checkSchemaMarkup(metrics);
  issues.push(...schemaIssues);
  score -= schemaIssues.filter(i => i.severity === 'critical').length * 5;
  score -= schemaIssues.filter(i => i.severity === 'warning').length * 2;

  // Check images optimization
  const imageOptScore = (metrics.webpImages / metrics.imageCount) * 100;
  if (imageOptScore < 80) {
    issues.push({
      severity: 'warning',
      category: 'Performance',
      message: `Only ${imageOptScore.toFixed(0)}% of images use WebP format`,
      recommendation: 'Convert more images to WebP for better performance'
    });
    score -= 3;
  }

  // Check alt text
  const altTextScore = (metrics.imagesWithAlt / metrics.imageCount) * 100;
  if (altTextScore < 100) {
    issues.push({
      severity: 'warning',
      category: 'Accessibility',
      message: `${(100 - altTextScore).toFixed(0)}% of images missing alt text`,
      recommendation: 'Add alt text to all images'
    });
    score -= 4;
  }

  // Check internal linking
  if (metrics.internalLinkCount < 10) {
    issues.push({
      severity: 'warning',
      category: 'Internal Linking',
      message: `Only ${metrics.internalLinkCount} internal links found`,
      recommendation: 'Add more internal links to improve site structure'
    });
    score -= 3;
  }

  // Ensure score is between 0 and 100
  score = Math.max(0, Math.min(100, score));

  return {
    cityId: city.id,
    cityName: city.name,
    language,
    score,
    issues,
    metrics,
    timestamp: new Date()
  };
};

/**
 * Analyze city page metrics (simulated)
 */
const analyzeCityPage = async (
  city: City,
  language: CityLanguage
): Promise<SEOMetrics> => {
  // In production, this would parse actual HTML/DOM
  // For now, we'll generate realistic mock data based on expected content
  
  const baseWordCount = 1500 + Math.floor(Math.random() * 1000);
  const keywordCount = Math.floor(baseWordCount * 0.02); // ~2% density
  
  return {
    wordCount: baseWordCount,
    keywordDensity: (keywordCount / baseWordCount) * 100,
    h1Count: 1,
    h2Count: 19 + Math.floor(Math.random() * 5), // 19-23 sections
    h3Count: 65 + Math.floor(Math.random() * 20), // 65-84 subsections
    h3PerH2Ratio: (65 + Math.floor(Math.random() * 20)) / (19 + Math.floor(Math.random() * 5)),
    
    titleLength: 55 + Math.floor(Math.random() * 10),
    metaDescriptionLength: 145 + Math.floor(Math.random() * 20),
    hasCanonical: Math.random() > 0.05, // 95% have canonical
    hasOpenGraph: Math.random() > 0.1, // 90% have OG
    hasTwitterCard: Math.random() > 0.2, // 80% have Twitter
    
    hasSchemaFAQ: Math.random() > 0.05,
    hasSchemaMedicalBusiness: Math.random() > 0.05,
    hasSchemaBreadcrumb: Math.random() > 0.05,
    hasSchemaOffer: Math.random() > 0.1,
    hasSchemaLocalBusiness: Math.random() > 0.1,
    hasSchemaReview: Math.random() > 0.15,
    
    internalLinkCount: 15 + Math.floor(Math.random() * 20),
    externalLinkCount: 2 + Math.floor(Math.random() * 3),
    brokenLinkCount: Math.random() > 0.9 ? Math.floor(Math.random() * 2) : 0,
    
    imageCount: 20 + Math.floor(Math.random() * 10),
    lazyLoadedImages: 18 + Math.floor(Math.random() * 10),
    webpImages: 16 + Math.floor(Math.random() * 10),
    
    imagesWithAlt: 19 + Math.floor(Math.random() * 10),
    semanticHTMLScore: 85 + Math.floor(Math.random() * 15)
  };
};

/**
 * Check schema markup completeness
 */
const checkSchemaMarkup = (metrics: SEOMetrics): SEOIssue[] => {
  const issues: SEOIssue[] = [];

  if (!metrics.hasSchemaFAQ) {
    issues.push({
      severity: 'critical',
      category: 'Schema Markup',
      message: 'FAQPage schema is missing',
      recommendation: 'Add FAQPage schema with 25-30 questions'
    });
  }

  if (!metrics.hasSchemaMedicalBusiness) {
    issues.push({
      severity: 'critical',
      category: 'Schema Markup',
      message: 'MedicalBusiness schema is missing',
      recommendation: 'Add MedicalBusiness schema'
    });
  }

  if (!metrics.hasSchemaBreadcrumb) {
    issues.push({
      severity: 'warning',
      category: 'Schema Markup',
      message: 'BreadcrumbList schema is missing',
      recommendation: 'Add breadcrumb schema for better navigation'
    });
  }

  if (!metrics.hasSchemaOffer) {
    issues.push({
      severity: 'warning',
      category: 'Schema Markup',
      message: 'Offer schema is missing',
      recommendation: 'Add Offer schema for pricing information'
    });
  }

  if (!metrics.hasSchemaLocalBusiness) {
    issues.push({
      severity: 'warning',
      category: 'Schema Markup',
      message: 'LocalBusiness schema is missing',
      recommendation: 'Add LocalBusiness schema per category'
    });
  }

  if (!metrics.hasSchemaReview) {
    issues.push({
      severity: 'info',
      category: 'Schema Markup',
      message: 'Review schema is missing',
      recommendation: 'Add Review schema for social proof'
    });
  }

  return issues;
};

/**
 * Batch audit multiple cities
 */
export const batchAuditCities = async (
  cities: City[],
  language: CityLanguage = 'en',
  onProgress?: (current: number, total: number) => void
): Promise<SEOAuditResult[]> => {
  const results: SEOAuditResult[] = [];
  
  for (let i = 0; i < cities.length; i++) {
    const result = await auditCityLanding(cities[i], language);
    results.push(result);
    
    if (onProgress) {
      onProgress(i + 1, cities.length);
    }
  }
  
  return results;
};

/**
 * Calculate aggregate statistics from audit results
 */
export interface AggregateStats {
  totalCities: number;
  averageScore: number;
  averageWordCount: number;
  averageKeywordDensity: number;
  citiesWithIssues: number;
  criticalIssuesCount: number;
  warningIssuesCount: number;
  scoreDistribution: {
    excellent: number; // 90-100
    good: number; // 75-89
    fair: number; // 60-74
    poor: number; // 0-59
  };
}

export const calculateAggregateStats = (
  results: SEOAuditResult[]
): AggregateStats => {
  const totalCities = results.length;
  const averageScore = results.reduce((sum, r) => sum + r.score, 0) / totalCities;
  const averageWordCount = results.reduce((sum, r) => sum + r.metrics.wordCount, 0) / totalCities;
  const averageKeywordDensity = results.reduce((sum, r) => sum + r.metrics.keywordDensity, 0) / totalCities;
  
  const citiesWithIssues = results.filter(r => r.issues.length > 0).length;
  const criticalIssuesCount = results.reduce(
    (sum, r) => sum + r.issues.filter(i => i.severity === 'critical').length,
    0
  );
  const warningIssuesCount = results.reduce(
    (sum, r) => sum + r.issues.filter(i => i.severity === 'warning').length,
    0
  );

  const scoreDistribution = {
    excellent: results.filter(r => r.score >= 90).length,
    good: results.filter(r => r.score >= 75 && r.score < 90).length,
    fair: results.filter(r => r.score >= 60 && r.score < 75).length,
    poor: results.filter(r => r.score < 60).length
  };

  return {
    totalCities,
    averageScore,
    averageWordCount,
    averageKeywordDensity,
    citiesWithIssues,
    criticalIssuesCount,
    warningIssuesCount,
    scoreDistribution
  };
};
